// src/oura/screens/allClients/model.ts
//
// The words and sums behind All clients (docs phase 1, workstream 8). Every
// count comes from route 11 (`GET /documents/summary`) as the server sends
// it; this file only adds lists up, picks the oldest, and words the result.
// A client whose folder is not linked, or linked and never read, has counts
// nobody has measured: they are left out of every sum and shown as unknown.

import type { AcrossClient, AcrossRow, DocsAcrossClients, NotSentCode } from "@service/aeo/clientDocs";
import { plural } from "@oura/docs";
import type { Tone } from "@oura/ui";
import { ouraHref } from "../../nav";
import { docToSendName, docsToSend, lateText, myToday } from "../../components/docs/boardModel";

/* ------------------------------------------------------------------------- *
 * The view filter, kept in the URL as ?show=
 * ------------------------------------------------------------------------- */

/** The groups, in the morning order: answers to read, late docs, docs we owe, then docs out with clients. */
export type GroupKey = "needs-us" | "late" | "not-sent-yet" | "waiting";
export type ShowKey = "all" | GroupKey;

export const GROUP_ORDER: GroupKey[] = ["needs-us", "late", "not-sent-yet", "waiting"];

/** "?show=late" to "late"; anything else is All. */
export function readShow(raw: string | null): ShowKey {
    return raw && (GROUP_ORDER as string[]).includes(raw) ? (raw as GroupKey) : "all";
}

/* ------------------------------------------------------------------------- *
 * Each client's folder
 * ------------------------------------------------------------------------- */

export type FolderState = "not_linked" | "not_read" | "cant_read" | "connected";

/**
 * Where the client's folder stands. `readAt` and `folderError` are optional
 * on the contract today: when the server leaves them out, a linked folder
 * reads as connected and its counts are taken as read.
 */
export function folderState(c: AcrossClient): FolderState {
    if (!c.folderLinked) return "not_linked";
    if (c.folderError) return "cant_read";
    if (c.readAt === null) return "not_read";
    return "connected";
}

/**
 * True when the client's counts were measured: a linked folder with at least
 * 1 good read. A folder Oura can no longer read keeps its last good counts.
 */
export function countsKnown(c: AcrossClient): boolean {
    return c.folderLinked && c.readAt !== null;
}

/* ------------------------------------------------------------------------- *
 * Client identity colour
 * ------------------------------------------------------------------------- */

/**
 * Identity dots, never a status tone: amber, blue, green, gray and rose
 * already say who moves next or that something is late. Six hues that stay
 * apart at 8px; indigo and purple read as violet beside it.
 */
const CLIENT_TONES: Tone[] = ["violet", "teal", "orange", "fuchsia", "slate", "brown"];

function hashOf(id: string): number {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    return h;
}

/**
 * One colour per client. Each id starts from its own hashed slot, so a
 * client keeps its colour as others come and go; a clash moves to the next
 * free slot, so no two clients on screen share one while there are colours
 * left.
 */
export function clientTones(ids: readonly string[]): Map<string, Tone> {
    const out = new Map<string, Tone>();
    const used = new Set<number>();
    const n = CLIENT_TONES.length;
    Array.from(new Set(ids))
        .sort()
        .forEach((id) => {
            if (used.size >= n) used.clear();
            let slot = hashOf(id) % n;
            while (used.has(slot)) slot = (slot + 1) % n;
            used.add(slot);
            out.set(id, CLIENT_TONES[slot]);
        });
    return out;
}

/* ------------------------------------------------------------------------- *
 * Sums across clients
 * ------------------------------------------------------------------------- */

/** The middle value, rounded to a whole day; null for none. */
function medianOf(values: number[]): number | null {
    if (values.length === 0) return null;
    const s = [...values].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return Math.round(s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2);
}

export interface NotSentLine {
    client: AcrossClient;
    /** Codes in this line. */
    count: number;
    /** The earliest expected-by among them (YYYY-MM-DD); null when the server does not say. */
    dueBy: string | null;
    /** The docs that clear them, "Answer Plan (S1, S3, B1)"; null when the server sends no codes. */
    docs: string[] | null;
}

/** A client's codes not sent yet: all of them, those past their due date (late), and those still in time. */
export interface NotSentSplit {
    all: NotSentLine;
    late: NotSentLine | null;
    inTime: NotSentLine | null;
}

function lineOf(client: AcrossClient, codes: readonly NotSentCode[]): NotSentLine | null {
    if (codes.length === 0) return null;
    const dueBy = codes.map((c) => c.expectedBy).filter((d): d is string => !!d).sort()[0] ?? null;
    const docs = docsToSend(codes.map((c) => ({ ...c, state: "not_sent_yet" as const }))).map(docToSendName);
    return { client, count: codes.length, dueBy, docs };
}

/**
 * A client's codes not sent yet, split at their due dates: a code past its
 * due date is late (YW, 29 Sep 2026). From `notSentCodes` when the server
 * sends it. The older shape names only the oldest due date, so a client
 * whose oldest has passed counts 1 late code, the least it can be.
 */
export function notSentOf(c: AcrossClient, today: string = myToday()): NotSentSplit | null {
    if (!countsKnown(c) || c.notSentYet <= 0) return null;
    if (c.notSentCodes) {
        const all = lineOf(c, c.notSentCodes);
        if (!all) return null;
        return {
            all,
            late: lineOf(c, c.notSentCodes.filter((n) => n.late === true)),
            inTime: lineOf(c, c.notSentCodes.filter((n) => n.late !== true)),
        };
    }
    const dueBy = c.notSentOldestBy ?? null;
    const all: NotSentLine = { client: c, count: c.notSentYet, dueBy, docs: null };
    const overdue = dueBy !== null && dueBy < today;
    return {
        all,
        late: overdue ? { client: c, count: 1, dueBy, docs: null } : null,
        inTime: overdue ? (c.notSentYet > 1 ? { client: c, count: c.notSentYet - 1, dueBy: null, docs: null } : null) : all,
    };
}

/** A client's late count: docs late and codes past their due date. */
export function lateOf(c: AcrossClient, today: string = myToday()): number {
    return c.late + (notSentOf(c, today)?.late?.count ?? 0);
}

export interface Summary {
    clients: number;
    linked: number;
    notLinked: number;
    notRead: number;
    cantRead: number;
    /** Clients whose counts were measured. */
    known: number;
    needsUs: AcrossRow[];
    late: AcrossRow[];
    waiting: AcrossRow[];
    /** Clients with codes due and no doc shared, oldest due first, then most codes. */
    notSent: NotSentLine[];
    notSentTotal: number;
    /** Clients with codes past their due date: the late codes, oldest due first. */
    lateCodes: NotSentLine[];
    lateCodesTotal: number;
    /** Clients with codes due and still in time, for the All view (the late ones show under Late there). */
    notSentInTime: NotSentLine[];
    /** Working days the oldest doc has waited on a client; null when none waits or the count is unknown. */
    oldestWaiting: number | null;
    /** Working days the oldest answer has waited on us. */
    oldestNeedsUs: number | null;
    /** The median of each client's typical reply time, over clients with an answered doc. */
    typicalReply: number | null;
    /** How many clients that median is taken across. */
    typicalReplyClients: number;
}

/** The first known count in a list: the lists arrive oldest first (late: most late first), so it is the oldest or the most late. */
export function firstDays(rows: readonly AcrossRow[], pick: (r: AcrossRow) => number | null): number | null {
    for (const r of rows) {
        const v = pick(r);
        if (v !== null) return v;
    }
    return null;
}

/** The most overdue first; a line whose due date is unknown goes after the dated ones. */
const byDue = (a: NotSentLine, b: NotSentLine) =>
    (a.dueBy ?? "9999").localeCompare(b.dueBy ?? "9999") || b.count - a.count || a.client.name.localeCompare(b.client.name);

export function summarise(data: DocsAcrossClients, today: string = myToday()): Summary {
    const clients = data.clients;
    const states = clients.map(folderState);
    const knownClients = clients.filter(countsKnown);
    const splits = knownClients.map((c) => notSentOf(c, today)).filter((x): x is NotSentSplit => x !== null);
    const notSent = splits.map((x) => x.all).sort(byDue);
    const lateCodes = splits.map((x) => x.late).filter((x): x is NotSentLine => x !== null).sort(byDue);
    const notSentInTime = splits.map((x) => x.inTime).filter((x): x is NotSentLine => x !== null).sort(byDue);
    const typicals = knownClients.map((c) => c.typicalReplyWorkingDays).filter((d): d is number => d !== null);
    return {
        clients: clients.length,
        linked: clients.filter((c) => c.folderLinked).length,
        notLinked: states.filter((s) => s === "not_linked").length,
        notRead: states.filter((s) => s === "not_read").length,
        cantRead: states.filter((s) => s === "cant_read").length,
        known: knownClients.length,
        needsUs: data.needsUs,
        late: data.late,
        waiting: data.waitingOnClient,
        notSent,
        notSentTotal: notSent.reduce((n, l) => n + l.count, 0),
        lateCodes,
        lateCodesTotal: lateCodes.reduce((n, l) => n + l.count, 0),
        notSentInTime,
        // The lists arrive oldest first (late: most late first), so the first known value is the answer.
        oldestWaiting: firstDays(data.waitingOnClient, (r) => r.waitingWorkingDays),
        oldestNeedsUs: firstDays(data.needsUs, (r) => r.waitingWorkingDays),
        typicalReply: medianOf(typicals),
        typicalReplyClients: typicals.length,
    };
}

/** The doc lists as the All view shows them: each doc once. */
export interface MorningRows {
    needsUs: AcrossRow[];
    late: AcrossRow[];
    waiting: AcrossRow[];
    /** Late docs left out of Late because they show under Needs us. */
    lateUnderNeedsUs: number;
    /** Docs waiting on a client left out of Waiting because they show under Late. */
    waitingUnderLate: number;
}

/**
 * The All view lists every group, so each doc shows once, in the first card
 * of the morning order it belongs to. Late is a flag on a doc that needs us
 * or waits on a client (route 11's lists overlap), so a late doc that needs
 * us stays under Needs us with its late note, and a late doc waiting on a
 * client shows under Late only. The filtered views, the tiles and the filter
 * counts keep the full lists.
 */
export function morningRows(s: Summary): MorningRows {
    const shown = new Set<string>();
    const once = (rows: AcrossRow[]) => {
        const kept = rows.filter((r) => !shown.has(r.documentId));
        rows.forEach((r) => shown.add(r.documentId));
        return kept;
    };
    const needsUs = once(s.needsUs);
    const late = once(s.late);
    const waiting = once(s.waiting);
    return {
        needsUs,
        late,
        waiting,
        lateUnderNeedsUs: s.late.length - late.length,
        waitingUnderLate: s.waiting.length - waiting.length,
    };
}

/** How many each group holds, for the filter and the tiles: docs, and for Late and Not sent yet the codes too. */
export function groupCount(s: Summary, key: GroupKey): number {
    if (key === "needs-us") return s.needsUs.length;
    if (key === "late") return s.late.length + s.lateCodesTotal;
    if (key === "waiting") return s.waiting.length;
    return s.notSentTotal;
}

/* ------------------------------------------------------------------------- *
 * Words
 * ------------------------------------------------------------------------- */

/**
 * The screen's one sentence, in the morning order: what needs us, what is
 * late (docs and codes past due), what we have not sent, then what waits on
 * clients and for how long, then any client Oura cannot count. Late is its
 * own sentence: it overlaps the others.
 * Never "nothing waiting" for a client it has not read.
 */
export function factsSentence(s: Summary): string {
    if (s.clients === 0) return "No clients yet.";
    if (s.linked === 0) return "No client folder is linked yet.";
    if (s.known === 0) return "Oura has not read a client folder yet.";
    const parts: string[] = [];
    const us = s.needsUs.length;
    if (us > 0) parts.push(`${plural(us, "doc")} ${us === 1 ? "needs" : "need"} us.`);
    // A code not sent by its due date is late too (YW, 29 Sep 2026).
    const late = lateText(s.late.length, s.lateCodesTotal);
    if (late) parts.push(late);
    // What we owe: codes whose plan month has started with no doc shared.
    if (s.notSentTotal > 0) parts.push(`${plural(s.notSentTotal, "code")} to create.`);
    const n = s.waiting.length;
    if (n === 0) {
        if (us === 0) parts.push("Nothing waits on a client.");
    } else {
        parts.push(n === 1 ? "1 doc is waiting on a client." : `${n} docs are waiting on clients.`);
        if (s.oldestWaiting !== null) {
            const who = n === 1 ? "It" : "The oldest";
            // 0 working days is not "today" on a weekend or holiday: say how long, not which day.
            parts.push(s.oldestWaiting <= 0 ? `${who} has waited under 1 working day.` : `${who} has waited ${plural(s.oldestWaiting, "working day")}.`);
        }
    }
    if (s.notLinked > 0) parts.push(`${plural(s.notLinked, "client")} ${s.notLinked === 1 ? "has" : "have"} no folder linked.`);
    if (s.notRead > 0) parts.push(s.notRead === 1 ? "1 folder is not read yet." : `${s.notRead} folders are not read yet.`);
    if (s.cantRead > 0) parts.push(`Oura can't read ${plural(s.cantRead, "folder")}.`);
    return parts.join(" ");
}

/** The typical reply time for one client, and what it rests on. */
export function replyTimeOf(c: AcrossClient): { value: string | null; basis: string | null } {
    if (!c.folderLinked) return { value: null, basis: null };
    if (!countsKnown(c)) return { value: null, basis: "Not read yet" };
    if (c.typicalReplyWorkingDays !== null) {
        return {
            value: plural(c.typicalReplyWorkingDays, "working day"),
            basis: `Median of ${plural(c.replyBasis.answered, "reply", "replies")}`,
        };
    }
    if (c.replyBasis.sent > 0) return { value: "Never replied", basis: `${plural(c.replyBasis.sent, "doc")} sent` };
    return { value: null, basis: "No answered doc yet" };
}

/* ------------------------------------------------------------------------- *
 * Links
 * ------------------------------------------------------------------------- */

/** The business's Client tab, with the doc open when one is named: /oura/businesses/{id}/reviews?doc={docId}. */
export function reviewsHref(businessId: string, documentId?: string): string {
    const base = ouraHref("reviews", { role: "oura-staff", businessId: encodeURIComponent(businessId) });
    return documentId ? `${base}?doc=${encodeURIComponent(documentId)}` : base;
}

/** The business's Client tab with its Client folder card open, the cursor in the link field: ?folder=1. */
export function folderHref(businessId: string): string {
    return `${reviewsHref(businessId)}?folder=1`;
}
